import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import StatusBadge from '../shared/StatusBadge';
import { Plus, Search, Send, CheckCircle, Trash2, X, Eye } from 'lucide-react';
import EmailPreviewModal from '../shared/EmailPreviewModal';
import { buildInvoiceHTML } from '../../utils/printInvoice';
import './Invoices.css';

const card = { background: 'rgba(30,41,59,0.3)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '1rem' };
const input = { width: '100%', padding: '0.75rem 1rem', borderRadius: '0.75rem', background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', outline: 'none' };
const btn = { padding: '0.75rem 1.5rem', background: 'linear-gradient(to right, #10b981, #06b6d4)', color: '#020617', fontWeight: 600, borderRadius: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' };

export default function Invoices() {
  const { invoices, clients, settings, fetchInvoices, fetchClients, fetchSettings, addInvoice, editInvoice, reviseInvoice, mailInvoice, removeInvoice, projectTemplates, patchDocument } = useApp();
  const [filter, setFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [form, setForm] = useState({ clientId: '', discount: 0 });
  const [itemSearch, setItemSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [clientDocs, setClientDocs] = useState([]);
  const [payments, setPayments] = useState([]);

  // UI state for sending and messages
  const [sendLoadingId, setSendLoadingId] = useState(null);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text }
  const [payLink, setPayLink] = useState(null); // store a generated payment link to show/copy
  const [payInfo, setPayInfo] = useState(null); // { chargedAmount, netAmount, feeAmount, paymentMethod }
  const [includePayLink, setIncludePayLink] = useState(false);
  const [manualPayOpen, setManualPayOpen] = useState(false);
  const [manualPayInvoice, setManualPayInvoice] = useState(null);
  const [manualPayMode, setManualPayMode] = useState('full');
  const [manualPayAmount, setManualPayAmount] = useState('');

  const calcTotals = () => {
    const subtotal = selectedItems.reduce((s, i) => s + i.rate * i.quantity, 0);
    const discount = parseFloat(form.discount) || 0;
    const tax = Math.round((subtotal - discount) * ((settings?.taxRate || 9) / 100) * 100) / 100;
    return { subtotal, discount, tax, total: subtotal - discount + tax };
  };

  const { subtotal, discount, tax, total } = calcTotals();

  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [emailPreviewRequest, setEmailPreviewRequest] = useState(null);

  useEffect(() => { fetchInvoices(); fetchClients(); fetchSettings(); }, [fetchInvoices, fetchClients, fetchSettings]);

  // Fetch documents for selected client when editing an invoice
  useEffect(() => {
    const loadDocsAndPayments = async () => {
      try {
        if (!form.clientId) { setClientDocs([]); } else {
          const res = await import('../../services/api').then(m => m.getClientDocuments(form.clientId));
          setClientDocs(res || []);
        }
        if (editingInvoiceId) {
          try {
            const payRes = await import('../../services/api').then(m => m.getInvoicePayments(editingInvoiceId));
            setPayments(payRes || []);
          } catch (err) { console.error('Failed to load payments', err); setPayments([]); }
        } else {
          setPayments([]);
        }
      } catch (err) { console.error('Failed to load client documents or payments', err); setClientDocs([]); setPayments([]); }
    };
    loadDocsAndPayments();
  }, [form.clientId, editingInvoiceId]);

  const filtered = invoices.filter(inv => filter === 'all' || inv.status === filter);

  const toggleItem = (item) => {
    const exists = selectedItems.find(i => i.name === item.name);
    if (exists) setSelectedItems(selectedItems.filter(i => i.name !== item.name));
    else setSelectedItems([...selectedItems, { ...item, quantity: 1 }]);
  };

  const toggleCat = (cat) => setExpanded(prev => ({ ...prev, [cat]: !prev[cat] }));

  const handleCreate = async () => {
    if (!form.clientId || selectedItems.length === 0) return alert('Select client and items');
    try {
      // debug
      console.log('Saving invoice, editingId:', editingInvoiceId, 'editInvoice type:', typeof editInvoice, 'addInvoice type:', typeof addInvoice);
      // Coerce items to numbers and validate
      const payloadItems = selectedItems.map(i => ({ name: i.name, description: i.description || '', quantity: Number(i.quantity) || 1, rate: Number(i.rate) || 0 }));
      const bad = payloadItems.find(i => !Number.isFinite(i.rate) || i.rate < 0 || !Number.isFinite(i.quantity) || i.quantity < 1);
      if (bad) return alert('Invalid item quantities or rates');

      if (editingInvoiceId) {
        const editingInvoice = (invoices || []).find((x) => x._id === editingInvoiceId);
        if (editingInvoice && editingInvoice.status !== 'draft') {
          if (typeof reviseInvoice !== 'function') throw new TypeError('reviseInvoice is not a function');
          const revised = await reviseInvoice(editingInvoiceId, { clientId: form.clientId, items: payloadItems, discount: form.discount, notes: form.notes });
          const newInvoiceNumber = revised?.invoice?.invoiceNumber || revised?.invoice?._id || 'draft revision';
          alert(`Sent invoice is immutable. Created ${newInvoiceNumber} as a new draft revision.`);
        } else {
          if (typeof editInvoice !== 'function') throw new TypeError('editInvoice is not a function');
          await editInvoice(editingInvoiceId, { clientId: form.clientId, items: payloadItems, discount: form.discount, notes: form.notes });
        }
      } else {
        if (typeof addInvoice !== 'function') throw new TypeError('addInvoice is not a function');
        await addInvoice({ clientId: form.clientId, items: payloadItems, discount: form.discount });
      }
      setShowModal(false);
      setSelectedItems([]);
      setEditingInvoiceId(null);
    } catch (err) { console.error('Save invoice error:', err); alert(err.response?.data?.error || err.response?.data?.details || err.message || 'Error'); }
  };
  const template = selectedTemplate ? projectTemplates[selectedTemplate] : null;
  const filterItems = (items) => itemSearch ? items.filter(item => item.name.toLowerCase().includes(itemSearch.toLowerCase())) : items;

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700 }}>Invoices ({invoices.length})</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {payLink && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(255,255,255,0.03)', padding: '0.5rem 0.75rem', borderRadius: '0.5rem' }}>
              <a href={payLink} target="_blank" rel="noreferrer" style={{ color: '#34d399' }}>Open Pay Link</a>
              <button onClick={() => { navigator.clipboard.writeText(payLink); setMessage({ type: 'success', text: 'Payment link copied' }); setTimeout(() => setMessage(null), 3000); }} style={{ padding: '0.35rem 0.5rem', borderRadius: '0.375rem' }}>Copy</button>
              <button onClick={() => { setPayLink(null); setPayInfo(null); }} style={{ padding: '0.35rem 0.5rem', borderRadius: '0.375rem' }}>Close</button>
              {payInfo && (
                <div style={{ marginLeft: '1rem', fontSize: '0.875rem', color: '#94a3b8' }}>
                  <div><strong>Client pays:</strong> ${payInfo.chargedAmount}</div>
                  <div style={{ color: '#64748b' }}>You net ${payInfo.netAmount} (fees ${payInfo.feeAmount})</div>
                </div>
              )}
            </div>
          )}
          <button onClick={() => { setShowModal(true); setSelectedTemplate(''); setSelectedItems([]); setForm({ clientId: '', discount: 0 }); setItemSearch(''); setExpanded({}); setEditingInvoiceId(null); setIncludePayLink(false); }} style={btn}><Plus style={{ width: '1rem', height: '1rem' }} /> Create Invoice</button>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
        <select value={filter} onChange={e => setFilter(e.target.value)} style={{ ...input, maxWidth: '200px' }}>
          <option value="all">All Status</option>
          {['draft', 'sent', 'partial', 'paid', 'overdue'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {message && (
          <div style={{ padding: '0.5rem 0.75rem', borderRadius: '0.5rem', background: message.type === 'success' ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.08)', color: message.type === 'success' ? '#34d399' : '#f87171' }}>{message.text}</div>
        )}
      </div>

      <div style={{ ...card }} className="responsive-table">
        <table>
          <thead><tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
            {['Invoice', 'Client', 'Amount', 'Status', 'Actions'].map(h => <th key={h} style={{ textAlign: 'left', padding: '1rem 1.5rem', fontSize: '0.875rem', fontWeight: 500, color: '#94a3b8' }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {filtered.map(inv => (
              <tr key={inv._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <td data-label="Invoice" style={{ padding: '1rem 1.5rem', cursor: 'pointer' }} onClick={() => {
                  setShowModal(true);
                  setEditingInvoiceId(inv._id);
                  setIncludePayLink(true);
                  setSelectedItems((inv.items || []).map(i => ({ name: i.name, rate: i.rate, quantity: i.quantity || i.qty || 1 })));
                  setForm({ clientId: inv.client?._id || inv.client, discount: inv.discount || 0 });
                  setSelectedTemplate('');
                  setItemSearch('');
                  setExpanded({});
                }}><p style={{ fontWeight: 500 }}>{inv.invoiceNumber}</p></td>
                <td data-label="Client" style={{ padding: '1rem 1.5rem' }}>{inv.clientName}</td>
                <td data-label="Amount" style={{ padding: '1rem 1.5rem', fontWeight: 600 }}>${inv.total.toLocaleString()}</td>
                <td data-label="Status" style={{ padding: '1rem 1.5rem' }}><StatusBadge status={inv.status} /></td>
                <td data-label="Actions" style={{ padding: '1rem 1.5rem' }}>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {inv.status === 'draft' && <button disabled={sendLoadingId === inv._id} onClick={async () => {
                      // Open preview modal instead of immediate send - allow admin to edit email first
                      setEmailPreviewRequest({ type: 'invoice', clientName: inv.clientName, invoiceNumber: inv.invoiceNumber, amount: (payInfo && payInfo.chargedAmount) ? payInfo.chargedAmount : inv.total, dueDate: inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : '', invoiceLink: `${process.env.REACT_APP_CLIENT_URL || 'https://www.pagehyve.com'}/api/invoices/${inv._id}/public`, paymentUrl: payLink || null });
                      window.__emailPreviewInvoiceId = inv._id; // temporarily store for send
                      setEmailPreviewOpen(true);
                    }} style={{ padding: '0.5rem', background: 'rgba(16,185,129,0.1)', color: '#34d399', borderRadius: '0.5rem' }}><Send style={{ width: '1rem', height: '1rem' }} /></button>}
                    <button onClick={async () => {
                      // Generate a pay link on-demand (admin) and show it
                      try {
                        const create = await import('../../services/api').then(m => m.createInvoiceCheckout(inv._id));
                        if (create && create.url) {
                          setPayLink(create.url);
                          setPayInfo({ chargedAmount: create.chargedAmount, netAmount: create.netAmount, feeAmount: create.feeAmount, paymentMethod: create.paymentMethod });
                          setMessage({ type: 'success', text: 'Payment link generated' });
                          setTimeout(() => setMessage(null), 4000);
                        } else {
                          setMessage({ type: 'error', text: 'Could not create payment link' });
                          setTimeout(() => setMessage(null), 4000);
                        }
                      } catch (err) {
                        setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to create payment link' });
                        setTimeout(() => setMessage(null), 4000);
                      }
                    }} style={{ padding: '0.5rem', background: 'transparent', color: '#94a3b8', borderRadius: '0.5rem' }}>Get Pay Link</button>
                    <button onClick={() => {
                      // Preview the invoice in a printable window
                      const html = buildInvoiceHTML(inv, settings);
                      const w = window.open('', '_blank');
                      w.document.write(html);
                      w.document.close();
                    }} style={{ padding: '0.5rem', background: 'rgba(255,255,255,0.02)', color: '#94a3b8', borderRadius: '0.5rem' }} title="Preview"><Eye style={{ width: '1rem', height: '1rem' }} /></button>
                    {['sent', 'overdue', 'partial'].includes(inv.status) && (
                      <button
                        onClick={() => {
                          const remaining = Math.max(0, Number(inv.total || 0) - (Number(inv.paidAmount || 0) / 100));
                          setManualPayInvoice(inv);
                          setManualPayMode('full');
                          setManualPayAmount(remaining.toFixed(2));
                          setManualPayOpen(true);
                        }}
                        style={{ padding: '0.5rem', background: 'rgba(16,185,129,0.1)', color: '#34d399', borderRadius: '0.5rem' }}
                        title="Record manual payment"
                      >
                        <CheckCircle style={{ width: '1rem', height: '1rem' }} />
                      </button>
                    )}
                    <button onClick={async () => { if (!window.confirm('Delete this invoice?')) return; try { await removeInvoice(inv._id); await fetchInvoices(); setMessage({ type: 'success', text: 'Invoice deleted' }); setTimeout(() => setMessage(null), 4000); } catch (err) { setMessage({ type: 'error', text: 'Failed to delete invoice' }); setTimeout(() => setMessage(null), 4000); } }} style={{ padding: '0.5rem', background: 'rgba(239,68,68,0.1)', color: '#f87171', borderRadius: '0.5rem' }}><Trash2 style={{ width: '1rem', height: '1rem' }} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>No invoices</div>}
      </div>

      {showModal && (
        <div className="invoice-modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem', overflowY: 'auto' }}>
          <div className="invoice-modal-shell" style={{ width: '100%', maxWidth: '72rem', margin: '2rem 0', background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '1rem', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
            <div className="invoice-modal-header" style={{ padding: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{editingInvoiceId ? 'Edit Invoice' : 'Create Invoice'}</h2>
              <button onClick={() => { setShowModal(false); setEditingInvoiceId(null); }} style={{ padding: '0.5rem', color: '#94a3b8' }}><X style={{ width: '1.25rem', height: '1.25rem' }} /></button>
            </div>
            <div className="invoice-modal-content" style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
              {/* Left - Items */}
              <div className="invoice-modal-left" style={{ flex: 1, borderRight: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '1rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Client *</label>
                    <select value={form.clientId} onChange={e => setForm({ ...form, clientId: e.target.value })} style={{ ...input, padding: '0.5rem 0.75rem' }}>
                      <option value="">Choose client...</option>
                      {clients.map(c => <option key={c._id} value={c._id}>{c.name} - {c.company}</option>)}
                    </select>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Template</label>
                    <div className="invoice-template-buttons" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {Object.entries(projectTemplates).map(([key, tmpl]) => (
                        <button key={key} onClick={() => { setSelectedTemplate(key); setExpanded({}); }} style={{ padding: '0.5rem 0.75rem', borderRadius: '0.5rem', fontSize: '0.75rem', fontWeight: 500, background: selectedTemplate === key ? 'rgba(16,185,129,0.2)' : 'transparent', border: `1px solid ${selectedTemplate === key ? 'rgba(16,185,129,0.5)' : 'rgba(255,255,255,0.1)'}`, color: selectedTemplate === key ? '#34d399' : '#fff' }}>{tmpl.name}</button>
                      ))}
                    </div>
                  </div>
                  {template && (
                    <div style={{ position: 'relative' }}>
                      <Search style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', width: '1rem', height: '1rem', color: '#64748b' }} />
                      <input value={itemSearch} onChange={e => setItemSearch(e.target.value)} placeholder="Search items..." style={{ ...input, padding: '0.5rem 0.75rem 0.5rem 2.25rem', fontSize: '0.875rem' }} />
                    </div>
                  )}
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
                  {template && Object.entries(template.categories).map(([cat, items]) => {
                    const filteredCatItems = filterItems(items);
                    if (filteredCatItems.length === 0) return null;
                    const isExpanded = expanded[cat] !== false;
                    const selectedInCat = filteredCatItems.filter(item => selectedItems.find(s => s.name === item.name)).length;
                    return (
                      <div key={cat} style={{ marginBottom: '0.75rem', background: 'rgba(30,41,59,0.3)', borderRadius: '0.75rem', overflow: 'hidden' }}>
                        <button onClick={() => toggleCat(cat)} style={{ width: '100%', padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#fff' }}>
                          <span style={{ fontWeight: 600, textTransform: 'capitalize', fontSize: '0.875rem' }}>{cat}</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            {selectedInCat > 0 && <span style={{ fontSize: '0.75rem', padding: '0.125rem 0.5rem', borderRadius: '9999px', background: 'rgba(16,185,129,0.2)', color: '#34d399' }}>{selectedInCat}</span>}
                            <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{filteredCatItems.length}</span>
                            <span style={{ color: '#64748b', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
                          </div>
                        </button>
                        {isExpanded && (
                          <div style={{ padding: '0.5rem', display: 'grid', gridTemplateColumns: '1fr', gap: '0.25rem' }}>
                            {filteredCatItems.map((item, i) => {
                              const sel = selectedItems.find(s => s.name === item.name);
                              return (
                                <div key={i} onClick={() => toggleItem(item)} style={{ padding: '0.5rem 0.75rem', borderRadius: '0.375rem', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: sel ? 'rgba(16,185,129,0.15)' : 'rgba(15,23,42,0.5)', border: `1px solid ${sel ? 'rgba(16,185,129,0.4)' : 'transparent'}` }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <div style={{ width: '1rem', height: '1rem', borderRadius: '0.25rem', border: `1.5px solid ${sel ? '#10b981' : 'rgba(255,255,255,0.2)'}`, background: sel ? '#10b981' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{sel && <CheckCircle style={{ width: '0.625rem', height: '0.625rem', color: '#fff' }} />}</div>
                                    <span style={{ fontSize: '0.8125rem', color: sel ? '#fff' : '#cbd5e1' }}>{item.name}</span>
                                  </div>
                                  <span style={{ fontSize: '0.8125rem', color: '#94a3b8', fontWeight: 500 }}>${item.rate}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {!template && <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>Select a template to see items</div>}
                </div>
              </div>
              {/* Right - Cart */}
              <div className="invoice-modal-right" style={{ width: '320px', display: 'flex', flexDirection: 'column', background: 'rgba(15,23,42,0.3)' }}>
                <div style={{ padding: '1rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}><h3 style={{ fontWeight: 600, fontSize: '0.875rem' }}>Selected ({selectedItems.length})</h3></div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem' }}>
                  {selectedItems.length === 0 ? <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b', fontSize: '0.875rem' }}>Click items to add</div> : selectedItems.map((item, i) => (
                    <div key={i} style={{ padding: '0.75rem', background: 'rgba(30,41,59,0.5)', borderRadius: '0.5rem', marginBottom: '0.5rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                        <span style={{ fontSize: '0.8125rem', fontWeight: 500, flex: 1, paddingRight: '0.5rem' }}>{item.name}</span>
                        <button onClick={() => toggleItem(item)} style={{ color: '#f87171', padding: '0.125rem' }}><X style={{ width: '0.875rem', height: '0.875rem' }} /></button>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Qty:</span>
                          <input type="number" min="1" value={item.quantity} onChange={e => setSelectedItems(selectedItems.map(s => s.name === item.name ? { ...s, quantity: parseInt(e.target.value) || 1 } : s))} style={{ width: '3rem', padding: '0.25rem', textAlign: 'center', borderRadius: '0.25rem', background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: '0.75rem' }} />
                          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>× ${item.rate}</span>
                        </div>
                        <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#34d399' }}>${(item.rate * item.quantity).toLocaleString()}</span>
                      </div>
                    </div>
                  ))}

                  {/* Documents for this client - attach to invoice when editing */}
                  {editingInvoiceId && (
                    <div>
                      <div style={{ marginTop: '1rem', background: 'rgba(15,23,42,0.3)', borderRadius: '0.5rem', padding: '0.75rem' }}>
                        <h4 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600 }}>Documents (attach to invoice)</h4>
                        {clientDocs.length === 0 ? <div style={{ color: '#64748b', paddingTop: '0.75rem' }}>No documents for this client</div> : (
                          <div style={{ display: 'grid', gap: '0.5rem', marginTop: '0.5rem' }}>
                            {clientDocs.map(d => (
                              <div key={d._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem', borderRadius: '0.375rem', background: String(d.invoice || '') === String(editingInvoiceId) ? 'rgba(16,185,129,0.12)' : 'transparent' }}>
                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                  <a href={d.url} target="_blank" rel="noreferrer" style={{ color: '#34d399' }}>{d.originalName}</a>
                                </div>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                  <button onClick={async () => { await patchDocument(d._id, { invoice: String(d.invoice || '') === String(editingInvoiceId) ? null : editingInvoiceId }); const res = await import('../../services/api').then(m => m.getClientDocuments(form.clientId)); setClientDocs(res); }} style={{ padding: '0.35rem 0.5rem', borderRadius: '0.375rem' }}>{String(d.invoice || '') === String(editingInvoiceId) ? 'Unlink' : 'Attach'}</button>
                                  <a href={d.signedUrl || d.url} target="_blank" rel="noreferrer" style={{ padding: '0.35rem 0.5rem', borderRadius: '0.375rem', background: 'transparent', color: '#34d399' }}>Download</a>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div style={{ marginTop: '1rem', background: 'rgba(15,23,42,0.3)', borderRadius: '0.5rem', padding: '0.75rem' }}>
                        <h4 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600 }}>Payments</h4>
                        {payments.length === 0 ? <div style={{ color: '#64748b', paddingTop: '0.75rem' }}>No payments recorded</div> : (
                          <div style={{ display: 'grid', gap: '0.5rem', marginTop: '0.5rem' }}>
                            {payments.map(p => (
                              <div key={p._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem', borderRadius: '0.375rem', background: 'transparent' }}>
                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                  <div style={{ fontWeight: 700, color: '#34d399' }}>${((p.amount || 0) / 100).toFixed(2)}</div>
                                  <div style={{ fontSize: '0.875rem', color: '#94a3b8' }}>{p.payerEmail || ''}</div>
                                </div>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                  {p.receiptUrl && <a href={p.receiptUrl} target="_blank" rel="noreferrer" style={{ padding: '0.35rem 0.5rem', borderRadius: '0.375rem', background: 'transparent', color: '#34d399' }}>Receipt</a>}
                                  {p.status === 'succeeded' && (
                                    <button onClick={async () => {
                                      try {
                                        const promptAmount = prompt('Refund amount (leave blank for full):', ((p.amount || 0) / 100).toFixed(2));
                                        if (promptAmount === null) return; // cancelled by user
                                        const body = {};
                                        if (promptAmount !== '') body.amount = promptAmount;
                                        await import('../../services/api').then(m => m.refundPayment(p._id, body.amount));
                                        await fetchInvoices();
                                        const payRes = await import('../../services/api').then(m => m.getInvoicePayments(editingInvoiceId));
                                        setPayments(payRes || []);
                                        setMessage({ type: 'success', text: 'Refund issued' });
                                        setTimeout(() => setMessage(null), 4000);
                                      } catch (err) {
                                        console.error('Refund failed', err);
                                        setMessage({ type: 'error', text: err.response?.data?.error || 'Refund failed' });
                                        setTimeout(() => setMessage(null), 4000);
                                      }
                                    }} style={{ padding: '0.35rem 0.5rem', borderRadius: '0.375rem', background: 'rgba(239,68,68,0.08)', color: '#f87171' }}>Refund</button>
                                  )}
                                  <div style={{ color: '#64748b', fontSize: '0.875rem' }}>{new Date(p.createdAt).toLocaleString()}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                </div>
                <div style={{ padding: '1rem', borderTop: '1px solid rgba(255,255,255,0.1)', background: 'rgba(15,23,42,0.5)' }}>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Discount ($)</label>
                    <input type="number" min="0" value={form.discount} onChange={e => setForm({ ...form, discount: e.target.value })} style={{ width: '100%', padding: '0.5rem', borderRadius: '0.375rem', background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: '0.875rem' }} />
                    <div style={{ marginTop: '0.5rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', color: '#94a3b8' }}>
                        <input type="checkbox" checked={includePayLink} onChange={e => setIncludePayLink(e.target.checked)} />
                        <span style={{ fontSize: '0.875rem' }}>Include Pay Link in Preview / Email</span>
                      </label>
                    </div>
                  </div>
                  <div style={{ fontSize: '0.875rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}><span style={{ color: '#94a3b8' }}>Subtotal</span><span>${subtotal.toLocaleString()}</span></div>
                    {discount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}><span style={{ color: '#94a3b8' }}>Discount</span><span style={{ color: '#34d399' }}>-${discount}</span></div>}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}><span style={{ color: '#94a3b8' }}>Tax ({settings?.taxRate || 9}%)</span><span>${tax.toLocaleString()}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.5rem', borderTop: '1px solid rgba(255,255,255,0.1)', fontWeight: 700, fontSize: '1rem' }}><span>Total</span><span style={{ color: '#34d399' }}>${total.toLocaleString()}</span></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="invoice-modal-footer" style={{ padding: '1rem', borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', gap: '1rem' }}>
              <button onClick={() => setShowModal(false)} style={{ flex: 1, padding: '0.75rem', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.75rem', color: '#fff' }}>Cancel</button>              <button onClick={async () => {
                const client = clients.find(c => c._id === form.clientId) || {};
                const previewInv = {
                  invoiceNumber: 'PREVIEW',
                  clientName: client.name || '',
                  clientEmail: client.email || '',
                  clientCompany: client.company || '',
                  items: selectedItems.map(i => ({ name: i.name, rate: i.rate, quantity: i.quantity })),
                  subtotal,
                  discount,
                  tax,
                  total,
                  dueDate: new Date(Date.now() + (settings?.paymentTerms || 30) * 24 * 3600 * 1000)
                };
                let payUrl = null;
                // Generate pay link if requested (for existing invoices or simulated for preview)
                if (includePayLink && editingInvoiceId) {
                  try {
                    const res = await import('../../services/api').then(m => m.createInvoiceCheckout(editingInvoiceId));
                    if (res && res.url) {
                      payUrl = res.url;
                      previewInv.payUrl = payUrl;
                    }
                  } catch (err) { console.warn('Failed to generate pay link for preview', err); }
                }
                const html = buildInvoiceHTML(previewInv, settings, payUrl);
                const w = window.open('', '_blank');
                w.document.write(html);
                w.document.close();
              }} style={{ padding: '0.75rem', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.75rem', background: 'transparent', color: '#94a3b8' }}>Preview</button>              <button onClick={handleCreate} disabled={!form.clientId || selectedItems.length === 0} style={{ ...btn, flex: 1, justifyContent: 'center', opacity: (!form.clientId || selectedItems.length === 0) ? 0.5 : 1 }}>{editingInvoiceId ? `Save Changes ($${total.toLocaleString()})` : `Create Invoice ($${total.toLocaleString()})`}</button>
            </div>
          </div>
        </div>
      )}

      {manualPayOpen && manualPayInvoice && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: '1rem' }}>
          <div style={{ width: '100%', maxWidth: '28rem', background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '1rem', padding: '1rem' }}>
            <h3 style={{ margin: 0, marginBottom: '0.75rem' }}>Record Manual Payment</h3>
            <div style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '1rem' }}>
              {manualPayInvoice.invoiceNumber} • Remaining: ${Math.max(0, Number(manualPayInvoice.total || 0) - (Number(manualPayInvoice.paidAmount || 0) / 100)).toFixed(2)}
            </div>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.35rem', textTransform: 'uppercase' }}>Payment Type</label>
              <select value={manualPayMode} onChange={(e) => {
                const next = e.target.value;
                setManualPayMode(next);
                if (next === 'full') {
                  const remaining = Math.max(0, Number(manualPayInvoice.total || 0) - (Number(manualPayInvoice.paidAmount || 0) / 100));
                  setManualPayAmount(remaining.toFixed(2));
                }
              }} style={input}>
                <option value="full">Paid in full</option>
                <option value="partial">Partial payment</option>
              </select>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.35rem', textTransform: 'uppercase' }}>Amount (USD)</label>
              <input type="number" min="0.01" step="0.01" value={manualPayAmount} onChange={(e) => setManualPayAmount(e.target.value)} disabled={manualPayMode === 'full'} style={{ ...input, opacity: manualPayMode === 'full' ? 0.7 : 1 }} />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => { setManualPayOpen(false); setManualPayInvoice(null); }} style={{ flex: 1, padding: '0.7rem', borderRadius: '0.75rem', border: '1px solid rgba(255,255,255,0.15)', color: '#fff' }}>Cancel</button>
              <button onClick={async () => {
                try {
                  const remaining = Math.max(0, Number(manualPayInvoice.total || 0) - (Number(manualPayInvoice.paidAmount || 0) / 100));
                  const amount = manualPayMode === 'full' ? remaining : Number(manualPayAmount);
                  if (!Number.isFinite(amount) || amount <= 0) {
                    setMessage({ type: 'error', text: 'Enter a valid payment amount' });
                    setTimeout(() => setMessage(null), 4000);
                    return;
                  }
                  if (amount > remaining) {
                    setMessage({ type: 'error', text: 'Amount exceeds remaining balance' });
                    setTimeout(() => setMessage(null), 4000);
                    return;
                  }
                  const api = await import('../../services/api');
                  await api.recordManualInvoicePayment(manualPayInvoice._id, amount);
                  await fetchInvoices();
                  setManualPayOpen(false);
                  setManualPayInvoice(null);
                  setMessage({ type: 'success', text: amount >= remaining ? 'Invoice marked paid' : `Partial payment recorded: $${amount.toFixed(2)}` });
                  setTimeout(() => setMessage(null), 4000);
                } catch (err) {
                  setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to record manual payment' });
                  setTimeout(() => setMessage(null), 4000);
                }
              }} style={{ ...btn, flex: 1, justifyContent: 'center' }}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      <EmailPreviewModal 
        isOpen={emailPreviewOpen}
        onClose={() => { setEmailPreviewOpen(false); setEmailPreviewRequest(null); window.__emailPreviewInvoiceId = null; }}
        onConfirm={async (customHtml) => {
          try {
            setSendLoadingId(window.__emailPreviewInvoiceId);
            const res = await mailInvoice(window.__emailPreviewInvoiceId, customHtml, false);
            await fetchInvoices();
            setMessage({ type: 'success', text: 'Invoice sent successfully' });
            if (res && res.paymentUrl) setPayLink(res.paymentUrl);
            setTimeout(() => setMessage(null), 4000);
          } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to send invoice' });
            setTimeout(() => setMessage(null), 4000);
          } finally {
            setSendLoadingId(null);
            setEmailPreviewOpen(false);
            window.__emailPreviewInvoiceId = null;
            setEmailPreviewRequest(null);
          }
        }}
        clientName={emailPreviewRequest?.clientName}
        clientEmail={emailPreviewRequest?.clientEmail}
        previewRequest={emailPreviewRequest}
      />
    </div>
  );
}
